import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Search, Upload, X, Lock, FileText } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { api, errMsg } from "../lib/api";
import { AuthImage } from "./ProfileFields";

export const GOV_TYPES = ["option", "inhabitant", "personnel", "personnel_multi", "committee", "labeled_files", "fee"];
const KGD = "Barangay Kagawad", PB = "Punong Barangay";
const inputCls = "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-[var(--brand-primary)] focus:outline-none disabled:bg-slate-100";
const cache = {};
function useFetch(url) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let live = true;
    if (!cache[url]) {
      cache[url] = api.get(url).then((r) => r.data).catch(() => []);
      setTimeout(() => { delete cache[url]; }, 3000);
    }
    cache[url].then((d) => live && setRows(d));
    return () => { live = false; };
  }, [url]);
  return rows || [];
}
export const clearGovCache = () => Object.keys(cache).forEach((k) => delete cache[k]);
export const useOptions = (cat) => useFetch(`/options/${cat}`);
export const usePersonnel = () => useFetch("/personnel/options");
export const useCommittees = () => useFetch("/committees/lookup");
export const inPool = (p, pool) => p.status === "active" && ({ kagawad: p.position === KGD, kagawad_pb: [KGD, PB].includes(p.position), any: true, any_no_pb: p.position !== PB })[pool];

export function SearchSelect({ options, value, onChange, multi, disabled, testid, placeholder = "Select…" }) {
  const [q, setQ] = useState("");
  const sel = multi ? (value || []) : value ? [value] : [];
  const label = (id) => options.find((o) => o.id === id)?.label || "Unavailable record";
  const list = options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase()));
  const pick = (id) => {
    if (!multi) return onChange(id === value ? null : id);
    const next = sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id];
    onChange(next.length ? next : null);
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" disabled={disabled} data-testid={testid} className="w-full min-h-[38px] rounded-md border border-slate-300 bg-white px-3 py-2 text-left text-sm flex items-center justify-between gap-2 disabled:bg-slate-100">
          <span className={`flex flex-wrap gap-1 ${sel.length ? "" : "text-slate-400"}`}>
            {sel.length ? sel.map((id) => multi ? <span key={id} className="rounded bg-[var(--brand-soft)] px-1.5 py-0.5 text-xs">{label(id)}</span> : <span key={id}>{label(id)}</span>) : placeholder}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-2" align="start">
        <div className="relative mb-2"><Search className="absolute left-2 top-2 h-4 w-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="w-full rounded border pl-8 pr-2 py-1.5 text-sm" data-testid={`${testid}-search`} /></div>
        <div className="max-h-60 overflow-y-auto">
          {list.length === 0 && <p className="p-2 text-xs text-slate-500">No eligible options.</p>}
          {list.map((o) => (
            <button type="button" key={o.id} onClick={() => pick(o.id)} data-testid={`${testid}-opt-${o.id}`}
              className={`w-full text-left rounded px-2 py-1.5 text-sm hover:bg-slate-50 flex items-center gap-2 ${sel.includes(o.id) ? "bg-[var(--brand-soft)] font-medium" : ""}`}>
              {multi && <input type="checkbox" readOnly checked={sel.includes(o.id)} className="accent-[var(--brand-primary)]" />}
              <span>{o.label}{o.sub && <span className="block text-xs text-slate-500 font-normal">{o.sub}</span>}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function OptionSelect({ f, value, onChange, disabled, record }) {
  const opts = useOptions(f.category);
  const id = `field-${f.key}`;
  const val = f.by_id ? value : value;
  const known = opts.some((o) => (f.by_id ? o.id : o.name) === val);
  const histLabel = f.by_id ? record?.data?.[f.display] : val;
  return (
    <select id={id} data-testid={id} disabled={disabled} className={inputCls} value={val ?? ""} onChange={(e) => onChange(e.target.value || null)}>
      <option value="">Select…</option>
      {val && !known && <option value={val}>{histLabel} (no longer available)</option>}
      {opts.map((o) => <option key={o.id} value={f.by_id ? o.id : o.name}>{o.name}</option>)}
    </select>
  );
}

function InhabitantSelect({ f, data, setData, disabled }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => api.get("/residents/lookup", { params: { q, purpose: f.purpose } }).then((r) => setRows(r.data.filter((x) => x.eligible))).catch(() => setRows([])), 250);
    return () => clearTimeout(t);
  }, [q, open, f.purpose]);
  const name = data[f.display];
  return (
    <Popover open={open && !disabled} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" disabled={disabled} data-testid={`field-${f.key}`} className="w-full min-h-[38px] rounded-md border border-slate-300 bg-white px-3 py-2 text-left text-sm flex items-center justify-between disabled:bg-slate-100">
          <span className={name ? "" : "text-slate-400"}>{name || "Search the Inhabitants Registry…"}</span>
          {disabled ? <Lock className="h-3.5 w-3.5 text-slate-400" /> : <Search className="h-4 w-4 text-slate-400" />}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-2" align="start">
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type a name or Resident ID…" className="mb-2 w-full rounded border px-2 py-1.5 text-sm" data-testid="inhabitant-search-input" />
        <div className="max-h-60 overflow-y-auto divide-y" data-testid="inhabitant-results">
          {rows.length === 0 && <p className="p-2 text-xs text-slate-500">{f.purpose === "personnel" ? "No unregistered inhabitants match. Inhabitants already in Personnel & Officials are excluded." : "No inhabitants found."}</p>}
          {rows.map((r) => (
            <button type="button" key={r.id} data-testid={`inhabitant-option-${r.ref_no}`} onClick={() => { setData((d) => ({ ...d, [f.key]: r.id, [f.display]: r.name })); setOpen(false); }}
              className="w-full text-left px-2 py-1.5 text-sm hover:bg-[var(--brand-soft)]">
              <span className="font-medium">{r.name}</span> <span className="font-mono text-xs text-slate-500">{r.ref_no}</span>
              <span className="block text-xs text-slate-500">{r.birth_date || "—"} · Age {r.age ?? "—"}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function FilePreview({ url, local, mime, filename }) {
  if (local && mime?.startsWith("image/")) return <img src={local} alt={filename} className="h-14 w-14 rounded border object-cover" />;
  if (url && mime?.startsWith("image/")) return <AuthImage url={url} alt={filename} />;
  return <span className="flex h-14 w-14 flex-col items-center justify-center rounded border bg-slate-50 text-[10px] text-slate-500"><FileText className="h-5 w-5" />{mime === "application/pdf" ? "PDF" : "File"}</span>;
}

export function LabeledFiles({ f, value, onChange, module, record, onBusy, disabled }) {
  const items = value || [];
  const [local, setLocal] = useState({});
  const [busy, setBusy] = useState(false);
  const add = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true); onBusy?.(true);
    try {
      const r = await api.post(`/m/${module.key}/field-upload`, fd);
      setLocal((l) => ({ ...l, [r.data.file_id]: URL.createObjectURL(file) }));
      onChange([...items, { file_id: r.data.file_id, label: "", filename: file.name, mime: r.data.mime }]);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); onBusy?.(false); }
  };
  const set = (i, patch) => onChange(items.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  return (
    <div className="space-y-2" data-testid={`labeled-files-${f.key}`}>
      {items.map((x, i) => (
        <div key={x.file_id} className="flex items-center gap-2 rounded-md border p-2" data-testid={`labeled-file-${f.key}-${i}`}>
          <FilePreview local={local[x.file_id]} url={record && !local[x.file_id] ? `/m/${module.key}/records/${record.id}/files/${x.file_id}` : null} mime={x.mime} filename={x.filename} />
          <div className="flex-1 min-w-0">
            <input value={x.label} disabled={disabled} onChange={(e) => set(i, { label: e.target.value })} placeholder="File label (required)" aria-label="File label"
              className={`${inputCls} ${x.label.trim() ? "" : "border-red-400"}`} data-testid={`labeled-file-label-${f.key}-${i}`} />
            <p className="mt-1 truncate text-xs text-slate-500">{x.filename}</p>
          </div>
          {!disabled && <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i).length ? items.filter((_, j) => j !== i) : null)} aria-label="Remove file" className="text-red-700" data-testid={`labeled-file-remove-${f.key}-${i}`}><X className="h-4 w-4" /></button>}
        </div>
      ))}
      {!disabled && (
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-xs font-medium hover:bg-slate-50">
          <Upload className="h-3.5 w-3.5" />{busy ? "Uploading…" : "Add file (PDF, PNG, JPEG)"}
          <input type="file" accept="image/png,image/jpeg,application/pdf" className="hidden" disabled={busy} onChange={(e) => { add(e.target.files[0]); e.target.value = ""; }} data-testid={`labeled-file-input-${f.key}`} />
        </label>
      )}
    </div>
  );
}

function FeeInput({ f, data, setData, record }) {
  const docs = useOptions("document_type");
  const sel = docs.find((d) => d.id === data[f.fee_from]);
  const same = record && data[f.fee_from] === record.data[f.fee_from] && record.data.reference_fee != null;
  const fixed = same ? !record.data.fee_variable : sel ? !sel.variable : true;
  const configured = same ? record.data.reference_fee : sel?.fee;
  useEffect(() => {
    if (fixed && configured != null && data.fee_amount !== configured) setData((d) => ({ ...d, fee_amount: configured }));
  }, [fixed, configured, data.fee_amount, setData]);
  const peso = (v) => `₱${Number(v || 0).toLocaleString("en-PH", { minimumFractionDigits: 2 })}`;
  if (fixed) return <div className="rounded-md border border-dashed bg-slate-50 px-3 py-2 text-sm flex items-center gap-1" data-testid="field-fee_amount-readonly"><Lock className="h-3 w-3" />{configured != null ? `${peso(configured)} (fixed fee)` : "Select a document type"}</div>;
  return (
    <div>
      <input id="field-fee_amount" data-testid="field-fee_amount" type="number" min="0" step="0.01" className={inputCls} value={data.fee_amount ?? ""} onChange={(e) => setData((d) => ({ ...d, fee_amount: e.target.value }))} />
      <p className="mt-1 text-xs text-slate-500">Variable fee · reference {peso(configured)}</p>
    </div>
  );
}

export function GovField({ f, module, data, setData, record, onBusy }) {
  const set = (v) => setData((d) => {
    const next = { ...d, [f.key]: v };
    if (f.key === "proposal_origin") module.fields.filter((x) => x.origin).forEach((x) => { next[x.key] = null; });
    return next;
  });
  const people = usePersonnel();
  let el;
  if (f.type === "option") el = <OptionSelect f={f} value={data[f.key]} onChange={set} record={record} />;
  else if (f.type === "inhabitant") el = <InhabitantSelect f={f} data={data} setData={setData} disabled={!!(record && f.purpose === "personnel" && record.data.resident_id)} />;
  else if (f.type === "labeled_files") el = <LabeledFiles f={f} value={data[f.key]} onChange={set} module={module} record={record} onBusy={onBusy} />;
  else if (f.type === "fee") el = <FeeInput f={f} data={data} setData={setData} record={record} />;
  else el = <SearchSelect options={people.filter((p) => p.status === "active").map((p) => ({ id: p.id, label: p.name, sub: p.position }))} value={data[f.key]} onChange={set} multi={f.type === "personnel_multi"} testid={`field-${f.key}`} />;
  return (
    <div className={f.type === "labeled_files" ? "sm:col-span-2" : ""}>
      <p className="mb-1 text-xs font-medium text-slate-700">{f.label}{(f.required || f.type === "fee") && <span className="text-red-600">*</span>}</p>
      {el}
    </div>
  );
}

function OriginRow({ label, children, wide, testid }) {
  return <div className={wide ? "sm:col-span-2" : ""} data-testid={testid}><p className="mb-1 text-xs font-medium text-slate-700">{label}</p>{children}</div>;
}

export function OriginSection({ module, data, setData, record, onBusy }) {
  const rule = module.origin_rules?.[data.proposal_origin];
  const people = usePersonnel();
  const committees = useCommittees();
  const fl = (k) => module.fields.find((x) => x.key === k);
  const pb = people.filter((p) => p.status === "active" && p.position === PB);
  const pbId = pb.length === 1 ? pb[0].id : null;
  useEffect(() => {
    if (rule?.initiator === "auto_pb" && pbId && data.initiator_id !== pbId && !record?.data?.initiator_id) setData((d) => ({ ...d, initiator_id: pbId }));
  }, [rule, pbId, data.initiator_id, record, setData]);
  const excluded = (excl, d = data) => new Set(excl.flatMap((e) => (e === "initiator_id" ? [d.initiator_id] : d[e] || [])).filter(Boolean));
  const update = (key, v) => setData((d) => {
    const next = { ...d, [key]: v };
    rule.roles.forEach(([k, , excl]) => {
      const ex = excluded(excl, next);
      if (next[k]?.some((x) => ex.has(x))) next[k] = next[k].filter((x) => !ex.has(x)).length ? next[k].filter((x) => !ex.has(x)) : null;
    });
    if (key === "originating_committee_id" && next.referred_committee_id === v) next.referred_committee_id = null;
    return next;
  });
  const opts = useMemo(() => (pool, excl, selected) => {
    const ex = excluded(excl);
    const sel = [].concat(selected || []);
    return people.filter((p) => (inPool(p, pool) || sel.includes(p.id)) && !ex.has(p.id)).map((p) => ({ id: p.id, label: p.name, sub: p.position }));
  }, [people, data]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!rule) return null;
  const comOpts = (exclude, selected) => committees.filter((c) => (c.status === "Active" || c.id === selected) && c.id !== exclude).map((c) => ({ id: c.id, label: c.name, sub: c.status === "Active" ? `Chair: ${c.chair_name || "—"}` : "Inactive" }));
  const chair = committees.find((c) => c.id === data.originating_committee_id);
  const pbName = people.find((p) => p.id === (record?.data?.initiator_id || pbId))?.name;
  return (
    <fieldset className="sm:col-span-2 grid sm:grid-cols-2 gap-4 rounded-md border p-4" data-testid="origin-section">
      <legend className="px-1 text-sm font-semibold text-brand">{data.proposal_origin} — origin details</legend>
      {rule.committee && <>
        <OriginRow label="Originating Committee *" testid="origin-committee"><SearchSelect options={comOpts(null, data.originating_committee_id)} value={data.originating_committee_id} onChange={(v) => update("originating_committee_id", v)} testid="field-originating_committee_id" /></OriginRow>
        <OriginRow label="Committee Chairperson"><div className="rounded-md border border-dashed bg-slate-50 px-3 py-2 text-sm flex items-center gap-1" data-testid="derived-committee-chair"><Lock className="h-3 w-3" />{chair ? chair.chair_name || "No chairperson assigned" : "Select a committee"}</div></OriginRow>
      </>}
      {rule.initiator === "auto_pb" && <OriginRow label={`${rule.initiator_label}`}><div className="rounded-md border border-dashed bg-slate-50 px-3 py-2 text-sm flex items-center gap-1" data-testid="derived-initiator"><Lock className="h-3 w-3" />{pbName || (pb.length > 1 ? "More than one active Punong Barangay — resolve in Personnel & Officials" : "No active Punong Barangay registered")}</div></OriginRow>}
      {rule.initiator && rule.initiator !== "auto_pb" && <OriginRow label={`${rule.initiator_label} *`}><SearchSelect options={opts(rule.initiator, [], data.initiator_id)} value={data.initiator_id} onChange={(v) => update("initiator_id", v)} testid="field-initiator_id" /></OriginRow>}
      {rule.fixed && <OriginRow label="Initiating Source"><div className="rounded-md border border-dashed bg-slate-50 px-3 py-2 text-sm flex items-center gap-1" data-testid="derived-initiating-source"><Lock className="h-3 w-3" />{rule.fixed.initiating_source}</div></OriginRow>}
      {rule.extra.includes("petitioner") && <OriginRow label="Petitioner *"><input className={inputCls} value={data.petitioner || ""} onChange={(e) => update("petitioner", e.target.value)} data-testid="field-petitioner" /></OriginRow>}
      {rule.extra.includes("source_name") && <OriginRow label="Source Name / Organization *"><input className={inputCls} value={data.source_name || ""} onChange={(e) => update("source_name", e.target.value)} data-testid="field-source_name" /></OriginRow>}
      {rule.roles.map(([k, pool, excl]) => (
        <OriginRow key={k} label={fl(k).label} wide><SearchSelect multi options={opts(pool, excl, data[k])} value={data[k]} onChange={(v) => update(k, v)} testid={`field-${k}`} /></OriginRow>
      ))}
      {rule.extra.includes("proposal_date") && <OriginRow label="Date of Committee Proposal"><input type="date" className={inputCls} value={data.proposal_date || ""} onChange={(e) => update("proposal_date", e.target.value)} data-testid="field-proposal_date" /></OriginRow>}
      {rule.extra.includes("committee_ref") && <OriginRow label="Committee Resolution / Recommendation Reference"><input className={inputCls} value={data.committee_ref || ""} onChange={(e) => update("committee_ref", e.target.value)} data-testid="field-committee_ref" /></OriginRow>}
      {rule.extra.includes("committee_ref_files") && <OriginRow label="Committee Resolution / Recommendation File" wide><LabeledFiles f={fl("committee_ref_files")} value={data.committee_ref_files} onChange={(v) => update("committee_ref_files", v)} module={module} record={record} onBusy={onBusy} /></OriginRow>}
      {rule.extra.includes("supporting_docs") && <OriginRow label="Supporting Documents" wide><LabeledFiles f={fl("supporting_docs")} value={data.supporting_docs} onChange={(v) => update("supporting_docs", v)} module={module} record={record} onBusy={onBusy} /></OriginRow>}
      {rule.extra.includes("referred_committee_id") && <OriginRow label="Referred Committee"><SearchSelect options={comOpts(data.originating_committee_id, data.referred_committee_id)} value={data.referred_committee_id} onChange={(v) => update("referred_committee_id", v)} testid="field-referred_committee_id" /></OriginRow>}
    </fieldset>
  );
}
